import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles, Box, BodyText, Skeleton } from 'vopay';
import { Title, Button, Card, CircleButton, Table } from 'UI';
import { useClientData } from '@/providers/ClientData.provider';
import { useDialog } from '@/providers/Dialog.provider';
import { useToast } from '@/providers/Toast.provider';
import useFetchAuthorizedIpAddresses from '@/hooks/useApi/useFetchAuthorizedIpAddresses';
import useUpdateAuthorizedIpAddresses from '@/hooks/useApi/useUpdateAuthorizedIpAddresses';
import IpAddressDialog from './IpAddressDialog';
import useDeleteIpAddressDialog from './useDeleteIpAddressDialog';
import noAuthorizedIpsIcon from './noAuthorizedIps.svg';
import styles from './AuthorizedIpsPage.module.scss';

const cn = withStyles(styles);

// The backend stores one comma-separated string, and treats '*' as its "allow all IPs" wildcard
// — both an empty string and '*' mean "no restrictions" here.
function parseAuthorizedIps(raw: string | undefined): string[] {
  if (!raw || raw === '*') return [];

  return raw
    .split(',')
    .map((ip) => ip.trim())
    .filter((ip) => ip !== '');
}

export default function AuthorizedIpsPage() {
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.AuthorizedIpsSettings' });
  const { response } = useClientData();

  const {
    fetchingAuthorizedIps,
    authorizedIps: authorizedIpsRaw,
    fetchAuthorizedIps,
  } = useFetchAuthorizedIpAddresses();

  const { updatingAuthorizedIps, updateAuthorizedIps } = useUpdateAuthorizedIpAddresses();
  const confirmDeleteIp = useDeleteIpAddressDialog();
  const dialog = useDialog();
  const toast = useToast();

  const [fetchFailed, setFetchFailed] = useState(false);

  const authorizedIps = parseAuthorizedIps(authorizedIpsRaw);

  useEffect(() => {
    fetchAuthorizedIps({ PortalUser: response.LoginUserName })
      .then(() => setFetchFailed(false))
      .catch(() => {
        setFetchFailed(true);
        toast.neutral(t('fetchError', { defaultValue: 'Could not load your authorized IPs.' }));
      });
  }, []);

  // The backend requires AuthorizedIPs to be non-empty, so clearing the list is sent as its '*'
  // wildcard rather than an empty string.
  async function saveAuthorizedIps(nextIps: string[]) {
    await updateAuthorizedIps({
      AuthorizedIPs: nextIps.length > 0 ? nextIps.join(',') : '*',
      PortalUser: response.LoginUserName,
    });

    await fetchAuthorizedIps({ PortalUser: response.LoginUserName });
  }

  function openAddDialog() {
    dialog.open<void>(
      ({ onConfirm, onCancel }) => (
        <IpAddressDialog
          onConfirm={onConfirm}
          onCancel={onCancel}
          existingIps={authorizedIps}
          onSubmit={async (newIps) => {
            await saveAuthorizedIps([...authorizedIps, ...newIps.filter((ip) => !authorizedIps.includes(ip))]);
          }}
        />
      ),
      { bare: true, closeOnEscape: true },
    );
  }

  function openEditDialog(ip: string) {
    dialog.open<void>(
      ({ onConfirm, onCancel }) => (
        <IpAddressDialog
          onConfirm={onConfirm}
          onCancel={onCancel}
          existingIps={authorizedIps}
          editingIp={ip}
          onSubmit={async (newIps) => {
            await saveAuthorizedIps(authorizedIps.map((existing) => (existing === ip ? newIps[0] : existing)));
          }}
        />
      ),
      { bare: true, closeOnEscape: true },
    );
  }

  async function handleDeleteIp(ip: string) {
    const isLastIp = authorizedIps.length === 1;
    const confirmed = await confirmDeleteIp(ip, isLastIp);

    if (!confirmed) return;

    try {
      await saveAuthorizedIps(authorizedIps.filter((existing) => existing !== ip));
    } catch {
      toast.neutral(t('deleteError', { defaultValue: 'Could not remove {{ip}}. Please try again.', ip }));
    }
  }

  return (
    <Box flx="col" gapSize="40px">
      <Box flx="col" gapSize="8px">
        <Title level="1">{t('pageTitle', { defaultValue: 'Authorized IPs' })}</Title>

        <BodyText className={cn('description')}>
          {t('description', {
            defaultValue:
              'This list identifies the machines that are authorized to make API requests using your account. Requests from any other IP address are rejected.',
          })}
        </BodyText>
      </Box>

      <Box flx="col" gapSize="20px">
        <Box flx="row" ai="center" jc="sb">
          <Title level="4">{t('currentlyAuthorized', { defaultValue: 'Currently Authorized IPs' })}</Title>

          <Button
            colorScheme="primary"
            size="large"
            iconLeft="add"
            disabled={fetchingAuthorizedIps || fetchFailed || updatingAuthorizedIps}
            onClick={openAddDialog}
          >
            {t('addIpButton', { defaultValue: 'Add IP Address' })}
          </Button>
        </Box>

        <Card className={cn('card')}>
          {fetchingAuthorizedIps || updatingAuthorizedIps ? (
            <Skeleton height={56} count={4} className={cn('rowSkeleton')} />
          ) : fetchFailed ? (
            <Box flx="col" ai="center" jc="center" gapSize="16px" className={cn('emptyState')}>
              <BodyText>{t('fetchError', { defaultValue: 'Could not load your authorized IPs.' })}</BodyText>
            </Box>
          ) : authorizedIps.length === 0 ? (
            <Box flx="col" ai="center" jc="center" gapSize="24px" className={cn('emptyState')}>
              <img src={noAuthorizedIpsIcon} alt="" className={cn('emptyStateIcon')} />

              <Box flx="col" ai="center" gapSize="8px" className={cn('emptyStateText')}>
                <Title level="3">{t('EmptyState.title', { defaultValue: 'No IP restrictions yet' })}</Title>

                <div className={cn('emptyStateDescription')}>
                  {t('EmptyState.description', {
                    defaultValue:
                      'Your account currently accepts API requests from any IP address. Add your server addresses to restrict access. Once you add one, only the addresses you list will be able to call the API.',
                  })}
                </div>
              </Box>
            </Box>
          ) : (
            <Table
              id="authorized-ips"
              zebra
              columns={[
                {
                  id: 'ipAddress',
                  header: t('Table.ipAddress', { defaultValue: 'IP Address' }),
                  align: 'left',
                  field: 'ip',
                },
                {
                  id: 'actions',
                  header: t('Table.actions', { defaultValue: 'Actions' }),
                  align: 'right',
                  template: ({ data }) => (
                    <Box flx="row" gapSize="16px" jc="fe" width="100%">
                      <CircleButton
                        size="large"
                        colorScheme="action"
                        icon="edit"
                        aria-label={t('Table.editAction', { defaultValue: 'Edit {{ip}}', ip: data.ip })}
                        onClick={() => openEditDialog(data.ip)}
                      />

                      <CircleButton
                        size="large"
                        colorScheme="destructive"
                        icon="delete"
                        aria-label={t('Table.deleteAction', { defaultValue: 'Delete {{ip}}', ip: data.ip })}
                        onClick={() => handleDeleteIp(data.ip)}
                      />
                    </Box>
                  ),
                },
              ]}
              rows={authorizedIps.map((ip) => ({ id: ip, data: { ip } }))}
            />
          )}
        </Card>
      </Box>
    </Box>
  );
}
